import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportInvoiceService } from '../../services/exportInvoice.service';
import { PERMISSIONS } from '../../constants/permissions';
import { IExportInvoice } from '../../interfaces/exportInvoice.interface';
import { triggerDownload } from '../statutoryRegisters/shared';
import ExportInvoiceLinesPanel from './ExportInvoiceLinesPanel';
import { formatMoney } from '../../utils/format';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';

/** Export invoice header detail (15.1) -- mirrors PackingListDetail's load/single-panel shape,
 * plus a "Download PDF" button (blob download) and the inline-editable lines panel. */
const ExportInvoiceDetail: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [invoice, setInvoice] = useState<IExportInvoice | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setInvoice(await ExportInvoiceService.get(institutionId, id));
        } catch (e) {
            toast.showApiError(e, 'Failed to load export invoice');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = async () => {
        if (!invoice) return;
        setDownloading(true);
        try {
            const blob = await ExportInvoiceService.downloadPdf(institutionId, invoice.id);
            triggerDownload(blob, `commercial-invoice-${invoice.invoiceNumber}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!invoice) return <Box sx={{ p: 3 }}><Typography>Export invoice not found.</Typography></Box>;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.invDetail.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">{invoice.invoiceNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">{formatDate(invoice.invoiceDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button data-help="commercial.invDetail.download" startIcon={<FileDownloadIcon />} variant="outlined" disabled={downloading} onClick={handleDownload}>
                        {downloading ? 'Downloading…' : 'Download PDF'}
                    </Button>
                    {canManage && (
                        <Button data-help="commercial.invDetail.edit" startIcon={<EditIcon />} variant="outlined" onClick={() => navigate(`/commercial/export-invoices/${invoice.id}/edit`)}>
                            Edit
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Incoterm</Typography>
                        <Typography>{enumLabel(invoice.incoterm)}{invoice.incotermPlace ? ` — ${invoice.incotermPlace}` : ''}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Currency</Typography>
                        <Typography>{invoice.currencyCode ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Total Quantity</Typography>
                        <Typography>{invoice.totalQuantity}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                        <Typography>{formatMoney(invoice.totalAmount, invoice.currencyCode)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Port of Loading</Typography>
                        <Typography>{invoice.portOfLoading ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Port of Discharge</Typography>
                        <Typography>{invoice.portOfDischarge ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Final Destination</Typography>
                        <Typography>{invoice.finalDestination ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Country of Origin</Typography>
                        <Typography>{invoice.countryOfOrigin ?? '—'}</Typography>
                    </Grid>
                    {invoice.lcNumber && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Letter of Credit</Typography>
                            <Typography>{invoice.lcNumber}{invoice.lcDate ? ` (${formatDate(invoice.lcDate)})` : ''}</Typography>
                            {invoice.issuingBankName && (
                                <Typography variant="body2" color="text.secondary">{invoice.issuingBankName}</Typography>
                            )}
                        </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">Consignee</Typography>
                        <Typography>{invoice.consigneeName ?? '—'}</Typography>
                        <Typography variant="body2" color="text.secondary">{invoice.consigneeAddress ?? ''}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">Notify Party</Typography>
                        <Typography>{invoice.notifyPartyName ?? '—'}</Typography>
                        <Typography variant="body2" color="text.secondary">{invoice.notifyPartyAddress ?? ''}</Typography>
                    </Grid>
                    {invoice.exporterBankDetails && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Exporter Bank Details</Typography>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>{invoice.exporterBankDetails}</Typography>
                        </Grid>
                    )}
                    {invoice.remarks && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Remarks</Typography>
                            <Typography>{invoice.remarks}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <ExportInvoiceLinesPanel invoiceId={invoice.id} onLinesChanged={load} />
        </Box>
    );
};

export default ExportInvoiceDetail;
